/*
  LaplandSanta bundle offers: Buy 1 / 2 / 3 cards with a style picker per Santa. Add to cart sends exactly
  those variants in one Ajax Cart request (via @theme/ls-cart), so the drawer opens and the header count updates.
  Prices come from Liquid in the market currency; savings are recalculated here when styles change.
  The product price block at the top follows the selected offer (total, compare-at, "40% OFF | SAVE £24.00").
*/
import { StandardEvents } from '@shopify/events';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { enqueue, mutate, moneyFormatter } from '@theme/ls-cart';

/** @param {number[]} ids */
const tally = (ids) => {
  const counts = new Map();
  for (const id of ids) if (id) counts.set(id, (counts.get(id) || 0) + 1);
  return counts;
};

class LsBundleOffers extends HTMLElement {
  #section = null;
  #form = null;
  #data = { variants: [], money: '', discounts: false, addedText: 'Added' };
  #format = (cents) => String(cents);
  #timer = 0;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    try {
      this.#data = { ...this.#data, ...JSON.parse(this.querySelector('[data-ls-bundle-data]')?.textContent || '{}') };
    } catch {}
    this.#format = moneyFormatter(this.#data.money);
    this.addEventListener('change', this.#onChange);
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);

    const radios = [...this.querySelectorAll('.ls-bundle__radio')];
    if (radios.length && !radios.some((r) => r.checked)) {
      (radios.find((r) => r.dataset.default === 'true') ?? radios[0]).checked = true;
    }
    this.#refresh();
    customElements.whenDefined('quantity-selector-component').then(() => this.#syncForm());

    // Horizon handles submit on document in the capture phase; window capture runs first, so the bundle wins.
    this.#form = this.#productForm()?.querySelector('form') ?? null;
    window.addEventListener('submit', this.#onSubmit, { capture: true });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    window.removeEventListener('submit', this.#onSubmit, { capture: true });
  }

  get #offers() {
    return [...this.querySelectorAll('.ls-bundle__offer')];
  }

  get #selectedOffer() {
    return this.#offers.find((o) => o.querySelector('.ls-bundle__radio:checked')) ?? null;
  }

  #productForm() {
    const forms = [...(this.#section?.querySelectorAll('product-form-component') ?? [])];
    return forms.find((f) => f.dataset.productId === this.dataset.productId) ?? forms[0] ?? null;
  }

  /** Variant ids for every Santa in an offer. */
  #picks(offer) {
    const selects = [...offer.querySelectorAll('[data-ls-pick]')];
    if (selects.length) return selects.map((s) => Number(s.value));
    const id = Number(this.#productForm()?.querySelector('input[name="id"]')?.value) || this.#data.variants[0]?.id;
    return Array.from({ length: Number(offer.dataset.qty) || 1 }, () => id);
  }

  /** Prices one offer for the chosen styles, the way Shopify discounts each line. The free Santa is not counted. */
  #price(offer) {
    const pct = Number(offer.dataset.pct) || 0;
    const byId = new Map(this.#data.variants.map((v) => [v.id, v]));
    let plain = 0;
    let discounted = 0;
    let reference = 0;
    for (const [id, n] of tally(this.#picks(offer))) {
      const v = byId.get(id);
      if (!v) continue;
      plain += v.price * n;
      discounted += Math.round((v.price * n * (100 - pct)) / 100);
      reference += (v.compare > v.price ? v.compare : v.price) * n;
    }
    const total = this.#data.discounts && pct > 0 ? discounted : plain;
    return { plain, discounted, reference, total };
  }

  #pill(total, reference) {
    const save = reference - total;
    if (save <= 0 || reference <= 0) return '';
    return `${Math.floor((save * 100) / reference)}% OFF | SAVE ${this.#format(save)}`;
  }

  #refresh() {
    for (const offer of this.#offers) {
      const { plain, discounted, reference } = this.#price(offer);
      for (const [kind, total] of [
        ['plain', plain],
        ['discount', discounted],
      ]) {
        const set = (attr, text) => {
          const el = offer.querySelector(`[${attr}="${kind}"]`);
          if (el) el.textContent = text;
        };
        set('data-ls-total', this.#format(total));
        set('data-ls-compare', reference > total ? this.#format(reference) : '');
        // A fixed label ("EXTRA 10% OFF") stays as Liquid rendered it.
        if (!offer.querySelector(`[data-ls-pill="${kind}"][data-ls-pill-fixed]`)) set('data-ls-pill', this.#pill(total, reference));
      }
      for (const select of offer.querySelectorAll('[data-ls-pick]')) {
        const thumb = select.parentElement?.querySelector('[data-ls-thumb]');
        const v = this.#data.variants.find((x) => x.id === Number(select.value));
        if (thumb && v?.image && !thumb.src.endsWith(v.image)) thumb.src = v.image;
      }
    }

    const offer = this.#selectedOffer;
    if (offer) {
      const { total, reference } = this.#price(offer);
      const compare = reference > total ? this.#format(reference) : '';
      const pill = this.#pill(total, reference);
      const summary = this.querySelector('[data-ls-summary]');
      if (summary) {
        summary.querySelector('[data-ls-sum-total]').textContent = this.#format(total);
        summary.querySelector('[data-ls-sum-compare]').textContent = compare;
        summary.querySelector('[data-ls-sum-pill]').textContent = pill;
        summary.hidden = false;
      }
      // Product price block(s) of this product: price, compare-at and savings of the selected offer.
      for (const block of this.#section?.querySelectorAll(`product-price[data-product-id="${this.dataset.productId}"]`) ?? []) {
        const put = (sel, text) => {
          const el = block.querySelector(sel);
          if (el && el.textContent !== text) el.textContent = text;
          return el;
        };
        put('[data-ls-main-price]', this.#format(total));
        put('[data-ls-main-compare]', compare)?.closest('[data-ls-main-compare-group]')?.toggleAttribute('hidden', !compare);
        put('[data-ls-main-badge-text]', pill)?.closest('[data-ls-main-badge]')?.toggleAttribute('hidden', !pill);
      }
    }
    this.#syncForm();
  }

  /** Keeps Horizon's own form in step, so it still adds the right Santas if this script ever fails to load. */
  #syncForm() {
    const offer = this.#selectedOffer;
    const productForm = this.#productForm();
    if (!offer || !productForm) return;
    const picks = this.#picks(offer);
    const idInput = productForm.querySelector('input[name="id"]');
    if (idInput && picks[0]) idInput.value = String(picks[0]);
    const selector = productForm.querySelector('quantity-selector-component');
    if (typeof selector?.setValue === 'function') {
      selector.setValue(String(picks.length));
      selector.updateButtonStates?.();
      selector.dispatchEvent(new QuantitySelectorUpdateEvent(picks.length));
    } else {
      const input = productForm.querySelector('input[name="quantity"]');
      if (input) input.value = String(picks.length);
    }
  }

  #onChange = (event) => {
    if (event.target instanceof HTMLElement && event.target.matches('.ls-bundle__radio, [data-ls-pick]')) this.#refresh();
  };

  /** If a Horizon variant picker is still on the page, follow it. */
  #onProductSelect = (event) => {
    event.promise
      ?.then(({ detail }) => {
        const id = detail?.resource?.id;
        if (!id) return;
        for (const select of this.querySelectorAll('[data-ls-pick]')) select.value = String(id);
        this.#refresh();
      })
      .catch(() => {});
  };

  #onSubmit = (event) => {
    if (!this.isConnected || !this.#form || event.target !== this.#form || !this.#selectedOffer) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#addToCart();
  };

  #setBusy(productForm, busy) {
    if (busy) productForm.dataset.lsBusy = 'true';
    else delete productForm.dataset.lsBusy;
    const buttons = [
      ...productForm.querySelectorAll('button[name="add"]'),
      ...(this.#section?.querySelectorAll('.sticky-add-to-cart__button') ?? []),
    ];
    for (const b of buttons) busy ? b.setAttribute('aria-busy', 'true') : b.removeAttribute('aria-busy');
  }

  #message(productForm, text, isError) {
    const live = productForm.querySelector('[ref="liveRegion"]');
    const box = productForm.querySelector('[ref="addToCartTextError"]');
    if (live) live.textContent = text;
    if (!box) return;
    for (const node of [...box.childNodes]) if (node.nodeType === Node.TEXT_NODE) node.remove();
    if (isError) box.append(document.createTextNode(text));
    box.classList.toggle('hidden', !isError);
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => {
      box.classList.add('hidden');
      if (live) live.textContent = '';
    }, isError ? 10000 : 5000);
  }

  async #addToCart() {
    const productForm = this.#productForm();
    const offer = this.#selectedOffer;
    if (!productForm || !offer || productForm.dataset.lsBusy === 'true') return;
    const items = [...tally(this.#picks(offer))].map(([id, quantity]) => ({ id, quantity }));
    if (!items.length) return;

    this.#setBusy(productForm, true);
    try {
      const { ok, message } = await enqueue(() =>
        mutate(productForm, { items }, { context: 'product', detail: { productId: this.dataset.productId } })
      );
      this.#message(productForm, ok ? this.#data.addedText : message || 'Something went wrong. Please try again.', !ok);
    } finally {
      this.#setBusy(productForm, false);
    }
  }
}

if (!customElements.get('ls-bundle-offers')) customElements.define('ls-bundle-offers', LsBundleOffers);
